import { useEffect, useState } from "react";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { notify, notifyError } from "../../../utils/toast";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";
import type {
  GetStoreListQuery,
  OrdersByStoreIdQuery,
} from "../../../__generated__/graphql";

const ORDERS_BY_STORE_ID = gql`
  query ordersByStoreId($storeId: Int!) {
    ordersByStoreId(storeId: $storeId) {
      id
      user {
        id
        name
        phone
      }
      isCanceled
      isPackConfirmed
      totalQuantity
      amount
      createdAt
    }
  }
`;

const PARTIAL_REFUND_ORDER_BY_ADMIN = gql`
  mutation PartialRefundOrderByAdmin($orderId: Int!, $refundQuantity: Int!) {
    partialRefundOrderByAdmin(orderId: $orderId, refundQuantity: $refundQuantity) {
      ok
      error
    }
  }
`;

type StoreRow = NonNullable<
  NonNullable<GetStoreListQuery["getStoreList"]>["result"]
>[number];

type PackOrder = OrdersByStoreIdQuery["ordersByStoreId"][number];

export default function AdminPartialRefundOrder() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [refundQuantity, setRefundQuantity] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<PackOrder | null>(null);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data: storeData, loading: storeLoading, error: storeError } =
    useQuery<GetStoreListQuery>(GET_STORE_LIST);

  const [
    getOrdersByStoreId,
    {
      data: ordersData,
      loading: ordersLoading,
      refetch: refetchOrders,
    },
  ] = useLazyQuery<OrdersByStoreIdQuery>(ORDERS_BY_STORE_ID, {
    variables: { storeId: selectedStore?.id ?? 0 },
  });

  const [partialRefundOrder] = useMutation<
    { partialRefundOrderByAdmin: { ok: boolean; error?: string | null } },
    { orderId: number; refundQuantity: number }
  >(PARTIAL_REFUND_ORDER_BY_ADMIN);

  useEffect(() => {
    if (selectedStore?.id) {
      getOrdersByStoreId({ variables: { storeId: selectedStore.id } });
    }
  }, [selectedStore?.id, getOrdersByStoreId]);

  const fullList =
    storeData?.getStoreList?.ok ? storeData.getStoreList.result ?? [] : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const storeList = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false),
      )
    : fullList;

  const allOrders = ordersData?.ordersByStoreId ?? [];
  const filteredOrders = allOrders.filter(
    (order) => order.totalQuantity >= 2,
  ) as PackOrder[];

  const handleSelectStore = (store: StoreRow) => {
    setSelectedStore(store);
    setSelectedOrder(null);
    setRefundQuantity("");
  };

  const handleSelectOrder = (order: PackOrder) => {
    setSelectedOrder(order);
    setRefundQuantity("");
  };

  const handlePartialRefund = async () => {
    if (!selectedOrder) {
      notifyError("주문을 선택해주세요.");
      return;
    }

    const quantity = parseInt(refundQuantity, 10);
    if (!refundQuantity.trim() || isNaN(quantity) || quantity < 1) {
      notifyError("환불할 팩 개수를 입력해주세요. (1개 이상)");
      return;
    }

    if (quantity >= selectedOrder.totalQuantity) {
      notifyError(
        `환불 개수는 주문 전체 개수(${selectedOrder.totalQuantity}개)보다 적어야 합니다. 전체 환불은 주문 취소 페이지를 이용해주세요.`,
      );
      return;
    }

    if (
      !window.confirm(
        `해당 고객이 맞습니까? ${selectedOrder.totalQuantity}개 중 ${quantity}개를 부분환불합니다.`,
      )
    ) {
      return;
    }

    await partialRefundOrder({
      variables: { orderId: selectedOrder.id, refundQuantity: quantity },
      onCompleted: (res) => {
        if (res.partialRefundOrderByAdmin.ok) {
          notify(`${quantity}개가 부분환불되었습니다.`);
          setSelectedOrder(null);
          setRefundQuantity("");
          refetchOrders();
        } else {
          notifyError(
            res.partialRefundOrderByAdmin.error ?? "부분환불에 실패했습니다.",
          );
          refetchOrders();
        }
      },
      onError: () => {
        refetchOrders();
      },
    });
  };

  if (storeLoading) {
    return (
      <div className="w-full p-4 flex justify-center py-8">
        <LoadingSpinnerInline />
      </div>
    );
  }
  if (storeError) {
    return (
      <div className="w-full p-4 text-red-600">
        에러: {storeError.message}
      </div>
    );
  }
  if (!storeData?.getStoreList?.ok) {
    return (
      <div className="w-full p-4 text-red-600">
        {storeData?.getStoreList?.error ?? "매장 목록을 불러올 수 없습니다."}
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="주문 부분환불"
      description="결제사 환불 & DB 수정 & 쿠폰 취소 올인원 가능. 주문 개수 2개 이상만 표시됩니다. 매장 선택 후 주문을 선택하고 환불 개수를 입력하세요."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={storeList.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) => {
              const full = storeList.find((x) => x.id === store.id);
              if (full) handleSelectStore(full);
            }}
            emptyText={
              keyword
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "부분환불 (주문 개수 2개 이상)",
        emptyText: "왼쪽에서 매장을 선택하세요.",
        children: selectedStore ? (
          <div className="space-y-4">
            {ordersLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinnerInline />
              </div>
            ) : (
              <>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">선택 매장:</span>{" "}
                  {selectedStore.title}
                </div>

                {filteredOrders.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">
                    주문 개수가 2개 이상인 주문이 없습니다.
                  </p>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead>
                          <tr>
                            <th className="px-2 py-2 text-left font-medium text-gray-500">
                              주문자
                            </th>
                            <th className="px-2 py-2 text-left font-medium text-gray-500">
                              주문일시
                            </th>
                            <th className="px-2 py-2 text-left font-medium text-gray-500">
                              주문금액
                            </th>
                            <th className="px-2 py-2 text-left font-medium text-gray-500">
                              주문개수
                            </th>
                            <th className="px-2 py-2 text-left font-medium text-gray-500">
                              상태
                            </th>
                            <th className="px-2 py-2 text-left font-medium text-gray-500">
                              선택
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filteredOrders.map((order) => (
                            <tr key={order.id}>
                              <td className="px-2 py-2">
                                {order.user?.name} ({order.user?.phone})
                              </td>
                              <td className="px-2 py-2">
                                {order.createdAt
                                  ? new Date(
                                      order.createdAt,
                                    ).toLocaleString()
                                  : "—"}
                              </td>
                              <td className="px-2 py-2">{order.amount}</td>
                              <td className="px-2 py-2">
                                {order.totalQuantity}개
                              </td>
                              <td className="px-2 py-2">
                                <span
                                  className={clsx(
                                    "px-2 inline-flex leading-5 font-semibold rounded-full text-xs",
                                    order.isCanceled
                                      ? "bg-red-100 text-red-800"
                                      : "bg-green-100 text-green-800",
                                  )}
                                >
                                  {order.isCanceled ? "취소" : "결제완료"}
                                </span>
                              </td>
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  onClick={() => handleSelectOrder(order)}
                                  className={clsx(
                                    "p-2 rounded text-sm",
                                    selectedOrder?.id === order.id
                                      ? "bg-gray-500 text-white"
                                      : "bg-blue-500 text-white hover:bg-blue-600",
                                  )}
                                >
                                  선택
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {selectedOrder && (
                      <>
                        <div className="p-4 bg-gray-100 rounded-lg">
                          <h3 className="text-sm font-medium text-gray-800 mb-2">
                            선택된 주문: 전체 {selectedOrder.totalQuantity}개 중
                          </h3>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            환불할 팩 개수 (1개 이상,{" "}
                            {selectedOrder.totalQuantity - 1}개 이하)
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={selectedOrder.totalQuantity - 1}
                            value={refundQuantity}
                            placeholder="환불할 팩 개수"
                            onChange={(e) =>
                              setRefundQuantity(e.target.value)
                            }
                            className="border p-2 rounded w-full text-sm"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={handlePartialRefund}
                          className="w-full py-3 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600"
                        >
                          부분환불 처리
                        </button>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        ) : null,
      }}
    />
  );
}
